/**
 * flowTree.ts
 *
 * Walks a protocol's step graph (configuration.next_steps) from its start node
 * and flattens it into the ordered, indented row model the step rail renders.
 *
 * Conditional steps fan out into YES / NO branches that rejoin at their merge
 * point; each branch becomes an indented group under a branch label, so the
 * rail carries the same routing information the wide flow diagram used to,
 * inside a 200px column.
 *
 * Graph walking is unchanged from the previous ProtocolFlowView implementation.
 */

import type { GroupStepInstance, ProtocolStep } from "../../../../../../types/lab/ProtocolTypes";

// ── Flow tree ─────────────────────────────────────────────────

type FlowStepNode = { kind: "step"; step: ProtocolStep };

type FlowBranchNode = {
	kind: "branch";
	conditional: ProtocolStep;
	yes: FlowNode[];
	no: FlowNode[];
};

type FlowNode = FlowStepNode | FlowBranchNode;

type Adjacency = Map<number, Array<{ next: number; type: string }>>;

function reachableFrom(startNum: number | undefined, adj: Adjacency): number[] {
	const visited = new Set<number>();
	const queue = [startNum];
	const result: number[] = [];
	while (queue.length) {
		const cur = queue.shift()!;
		if (cur == null || visited.has(cur)) continue;
		visited.add(cur);
		result.push(cur);
		for (const edge of adj.get(cur) ?? []) queue.push(edge.next);
	}
	return result;
}

function findMergePoint(
	yesStart: number | undefined,
	noStart: number | undefined,
	adj: Adjacency,
): number | null {
	if (!yesStart || !noStart) return null;
	const yesReachable = new Set(reachableFrom(yesStart, adj));
	const noQueue = [noStart];
	const noVisited = new Set<number>();
	while (noQueue.length) {
		const cur = noQueue.shift()!;
		if (noVisited.has(cur)) continue;
		noVisited.add(cur);
		if (yesReachable.has(cur)) return cur;
		for (const edge of adj.get(cur) ?? []) noQueue.push(edge.next);
	}
	return null;
}

function walkLinear(
	startNum: number | null | undefined,
	stepByNum: Map<number, ProtocolStep>,
	adj: Adjacency,
	visited: Set<number>,
	stopAt: number | null,
): FlowNode[] {
	const result: FlowNode[] = [];
	let current: number | null | undefined = startNum;

	while (current != null && current !== stopAt) {
		if (visited.has(current)) break;
		const step = stepByNum.get(current);
		if (!step) break;

		visited.add(current);

		if (step.step_type === "conditional") {
			const nexts = adj.get(current) ?? [];
			const yesEdge = nexts.find((n) => n.type === "condition_yes");
			const noEdge = nexts.find((n) => n.type === "condition_no");
			const mergePoint = findMergePoint(yesEdge?.next, noEdge?.next, adj);

			result.push({
				kind: "branch",
				conditional: step,
				yes: walkLinear(yesEdge?.next, stepByNum, adj, new Set(visited), mergePoint),
				no: walkLinear(noEdge?.next, stepByNum, adj, new Set(visited), mergePoint),
			});

			current = mergePoint;
		} else {
			result.push({ kind: "step", step });
			const nexts = adj.get(current) ?? [];
			const defaultEdge = nexts.find((n) => n.type === "default") ?? nexts[0];
			current = defaultEdge?.next ?? null;
		}
	}

	return result;
}

function buildFlowTree(steps: ProtocolStep[]): FlowNode[] {
	if (!steps.length) return [];

	const stepByNum = new Map(steps.map((s) => [s.step_number, s]));
	const adj: Adjacency = new Map();
	for (const s of steps) {
		const nexts = (s.configuration?.next_steps ?? []) as Array<{
			next_step: number;
			connection_type?: string;
		}>;
		adj.set(
			s.step_number,
			nexts.map((n) => ({ next: n.next_step, type: n.connection_type ?? "default" })),
		);
	}

	const startStep = steps.find((s) => s.step_type === "start");
	if (!startStep) return [];

	return walkLinear(startStep.step_number, stepByNum, adj, new Set(), null);
}

// ── Rail rows ─────────────────────────────────────────────────

export type RailRow =
	| { kind: "step"; key: string; step: ProtocolStep; depth: number }
	| { kind: "branch"; key: string; label: "Yes" | "No"; tone: "yes" | "no"; depth: number };

/** Step types the rail hides — they carry no data and can never be opened. */
const RAIL_HIDDEN_TYPES = new Set(["start", "end"]);

function flatten(nodes: FlowNode[], depth: number, hidden: Set<number>): RailRow[] {
	const out: RailRow[] = [];

	for (const node of nodes) {
		if (node.kind === "step") {
			if (RAIL_HIDDEN_TYPES.has(node.step.step_type) || hidden.has(node.step.id)) continue;
			out.push({ kind: "step", key: `step-${node.step.id}`, step: node.step, depth });
			continue;
		}

		// The conditional itself stays even when both its branches are hidden — it
		// ran, and it is what explains why the path went the way it did.
		if (!hidden.has(node.conditional.id)) {
			out.push({
				kind: "step",
				key: `step-${node.conditional.id}`,
				step: node.conditional,
				depth,
			});
		}

		// A branch label only earns its row when the branch still has rows under it.
		const yesRows = flatten(node.yes, depth + 1, hidden);
		if (yesRows.length > 0) {
			out.push({
				kind: "branch",
				key: `yes-${node.conditional.id}`,
				label: "Yes",
				tone: "yes",
				depth,
			});
			out.push(...yesRows);
		}

		const noRows = flatten(node.no, depth + 1, hidden);
		if (noRows.length > 0) {
			out.push({
				kind: "branch",
				key: `no-${node.conditional.id}`,
				label: "No",
				tone: "no",
				depth,
			});
			out.push(...noRows);
		}
	}

	return out;
}

/**
 * Ordered rail rows for a protocol.
 *
 * `hidden` drops steps the group's routing has ruled out (see
 * `computeUntakenStepIds`), along with any branch label left empty by that.
 *
 * Falls back to step_number order when the graph cannot be walked (no start
 * node, or next_steps never wired up) so the rail always lists the protocol's
 * steps rather than rendering empty.
 */
export function buildRailRows(
	steps: ProtocolStep[],
	hidden: Set<number> = new Set(),
): RailRow[] {
	const rows = flatten(buildFlowTree(steps), 0, hidden);

	if (rows.length > 0) return rows;

	return [...steps]
		.filter((s) => !RAIL_HIDDEN_TYPES.has(s.step_type) && !hidden.has(s.id))
		.sort((a, b) => a.step_number - b.step_number)
		.map((step) => ({ kind: "step" as const, key: `step-${step.id}`, step, depth: 0 }));
}

// ── Branch reachability ───────────────────────────────────────

/** A step instance that actually ran, rather than a placeholder row. */
function hasRun(gsi: GroupStepInstance | undefined): boolean {
	return !!gsi && gsi.id > 0 && gsi.status !== "pending";
}

function collectStepIds(nodes: FlowNode[], out: Set<number>): void {
	for (const node of nodes) {
		if (node.kind === "step") {
			out.add(node.step.id);
			continue;
		}
		out.add(node.conditional.id);
		collectStepIds(node.yes, out);
		collectStepIds(node.no, out);
	}
}

/**
 * Steps this group will never reach, because the conditional above them routed
 * every instance down the other branch.
 *
 * Two signals, both written by the backend's auto-execution engine:
 *  - a step instance with an empty `applicable_instance_ids` — no instance
 *    applies to it, so it can never be completed;
 *  - a branch subtree that no instance is routed into, per the conditional's
 *    `sample_routing`.
 *
 * Execution history overrides both: a step that actually ran is always reachable,
 * whatever the routing now says.
 *
 * Used to keep untaken branches out of the step rail, and to stop them blocking
 * completion of a group whose live path is finished.
 */
export function computeUntakenStepIds(
	steps: ProtocolStep[],
	stepInstances: GroupStepInstance[],
): Set<number> {
	const untaken = new Set<number>();
	if (steps.length === 0) return untaken;

	const gsiByStepId = new Map(stepInstances.map((gsi) => [gsi.step_id, gsi]));

	// Signal 1 — the engine narrowed this step to zero instances.
	for (const step of steps) {
		const gsi = gsiByStepId.get(step.id);
		if (
			gsi &&
			Array.isArray(gsi.applicable_instance_ids) &&
			gsi.applicable_instance_ids.length === 0
		) {
			untaken.add(step.id);
		}
	}

	// Signal 2 — no instance took this branch of a decided conditional.
	const visit = (nodes: FlowNode[]): void => {
		for (const node of nodes) {
			if (node.kind !== "branch") continue;

			const routes = new Set(
				Object.values(gsiByStepId.get(node.conditional.id)?.sample_routing ?? {}),
			);
			// An undecided conditional records no routing — leave both branches in place.
			if (routes.size > 0) {
				if (!routes.has("condition_yes")) collectStepIds(node.yes, untaken);
				if (!routes.has("condition_no")) collectStepIds(node.no, untaken);
			}

			visit(node.yes);
			visit(node.no);
		}
	};
	visit(buildFlowTree(steps));

	// Anything that actually ran stays, whatever the routing says now.
	for (const id of [...untaken]) {
		if (hasRun(gsiByStepId.get(id))) untaken.delete(id);
	}

	return untaken;
}
